const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Script inline que avisa "este documento se mostró" (`kind:"document"` con el id de visita) UNA
 * sola vez, cuando la página está visible y no prerenderizada:
 *
 * - Visible al cargar ⇒ manda en el acto.
 * - Abierta en segundo plano o precargada ⇒ espera `visibilitychange` / `prerenderingchange` y
 *   marca `hiddenFirst`. Una precarga que nadie abre no manda nada: por eso no cuenta como visita.
 * - Vuelta desde el bfcache (`pageshow` con `persisted`) ⇒ no hay documento nuevo: va como `spa`,
 *   con un `eventId` propio (`crypto.randomUUID()` o, si no está, uno con `Math.random`).
 *
 * Transporte (diagnóstico del 2026-10-07: en el WebView de TikTok Android, cuando la red nativa
 * TTNet trae el HTML, la página corre y la ven, pero los POST no salen del teléfono —seguro
 * `sendBeacon`/`keepalive`, por lo visto también `fetch`— mientras que los GET sí llegan: los XHR
 * de axios de la app y los recursos). Cada aviso sale por los dos caminos A LA VEZ, con los mismos
 * ids (la API es idempotente):
 *
 * 1. `fetch` POST (body string ⇒ `text/plain`, sin preflight, sin `keepalive`). Confirma SÓLO con
 *    204: un 200 lo pudo inventar la red nativa.
 * 2. `<img>` a `GET /api/pv/r?k=…` (en el acto: el único camino probado en TTNet, y así también
 *    cuenta a quien se va antes de que conteste el POST). Confirma si carga el GIF.
 * 3. `sendBeacon` sólo si fallaron los dos: el `<img>` dio error (o no hay `Image`) y el POST
 *    terminó sin 204 (o no hubo `fetch`). Con el POST colgado no sale: la app (`VisitBeacon`)
 *    repite el `document` que no confirmó.
 *
 * Registra los listeners ANTES del primer intento: si algo tira al cargar, el aviso sale igual
 * cuando la página se vuelve visible.
 *
 * Deja el id en `window.__acdVid`: el beacon de Clarity, los clics del embudo y la confirmación de
 * la app (`VisitBeacon`) lo mandan para unirse al documento exacto (no sólo a la sesión). Cuando el
 * `document` confirma (204 del POST o el GIF), deja `window.__acdR = 1`: la app ya no lo repite.
 *
 * Va inline (no en el bundle) para no depender de la hidratación ni del consentimiento. Con un id
 * que no es UUID devuelve `""` (nada que inyectar). Tiene que quedar en < 1300 bytes (hoy ~1190).
 */
export function buildVisitRenderSnippet(visitId: string | null | undefined): string {
  if (typeof visitId !== "string" || !UUID_RE.test(visitId)) return "";
  return (
    `(function(){try{var v="${visitId}",u="/api/pv/r",s=0,d=document,n=navigator,` +
    `h=d.visibilityState!=="visible"||!!d.prerendering;window.__acdVid=v;` +
    `function send(o,q){var j=JSON.stringify(o),k=0,f=0,g=0;` +
    `function ok(){k=1;if(o.kind=="document")window.__acdR=1}` +
    `function b(){if(f&&g&&!k)try{n.sendBeacon(u,j)}catch(e){}}` +
    `function F(){f=1;b()}function G(){g=1;b()}` +
    `try{fetch(u,{method:"POST",body:j,credentials:"same-origin"})` +
    `.then(function(r){r&&r.status===204?ok():F()},F)}catch(e){F()}` +
    `try{var i=new Image();i.onload=ok;i.onerror=G;i.src=u+"?k="+o.kind+q+"&p="+encodeURIComponent(o.path)}catch(e){G()}}` +
    `function go(){if(s||d.prerendering||d.visibilityState!=="visible")return;var p=location.pathname;s=1;` +
    `send({kind:"document",visitId:v,path:p,hiddenFirst:h},"&v="+v+"&h="+(h?1:0))}` +
    `d.addEventListener("visibilitychange",go);d.addEventListener("prerenderingchange",go);` +
    `addEventListener("pageshow",function(e){if(!e.persisted)return;var x;` +
    `try{x=crypto.randomUUID()}catch(_){x="10000000-1000-4000-8000-100000000000".replace(/[018]/g,` +
    `function(c){return(c^Math.random()*16>>c/4).toString(16)})}` +
    `send({kind:"spa",path:location.pathname,eventId:x},"&e="+x)});` +
    `go()}catch(e){}})();`
  );
}
